import React, { useEffect, useState } from 'react';
import { DataProviderInstance } from '../../api/DataProvider';
import styles from './AnamnesisTemplates.module.css';
import Loading from '../../components/loading/Loading.js';
import {
    Box,
    Typography,
    Snackbar,
    Alert
} from '@mui/material';
import {
    Add as AddIcon,
    InfoOutlined as InfoOutlinedIcon
} from '@mui/icons-material/';
import CustomButton from '../../components/buttons/CustomButton';
import CreateEditAnamnesisTemplate from '../../popup/CreateEditAnamnesisTemplate/CreateEditAnamnesisTemplate';
import ConfirmPopup from '../../popup/ConfirmPopup/ConfirmPopup';
import AnamnesisCard from './components/AnamnesisCard';
import AnamnesisPreviewModal from './components/AnamnesisPreviewModal';

const AnamnesisTemplates = () => {
    const { GetAnamnesisTemplates, DeleteAnamnesisTemplate } = DataProviderInstance();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [confirmPopupData, setConfirmPopupData] = useState({title: "", description: ""});

    const [currentItem, setCurrentItem] = useState(null);
    const [isVisibleTemplateInfo, setIsVisibleTemplateInfo] = useState(false);
    const [isVisibleConfirmPopup, setIsVisibleConfirmPopup] = useState(false);
    const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    
    // Estado para notificações
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        loadAnamnesisTemplates(() => setLoading(false));
    }, []);

    const loadAnamnesisTemplates = (callback) => {
        GetAnamnesisTemplates((response) => {
            // Extract the data array from the response and ensure it's an array
            const templatesArray = response?.data && Array.isArray(response.data) ? response.data : [];
            setData(templatesArray);
            if(callback) callback();
        });
    }

    if (loading) {
        return <Loading />;
    }

    const handleAddTemplate = () => {
        setCurrentItem(null);
        setIsVisibleTemplateInfo(true);
    };

    const handleEditTemplate = (id) => {
        const itemToEdit = data.find((template) => template.id == id);
        setCurrentItem(itemToEdit);
        setIsVisibleTemplateInfo(true);
    };

    const handleDeleteTemplate = (id) => {
        const itemToDelete = data.find((template) => template.id == id);
        setCurrentItem(itemToDelete);

        setConfirmPopupData({
            title: "Apagar modelo",
            description: `Deseja realmente apagar o modelo <strong>${itemToDelete.label}</strong>?`
        });

        setIsVisibleConfirmPopup(true);
    };

    const handlePreviewTemplate = (template) => {
        setPreviewTemplate(template);
        setIsPreviewModalVisible(true);
    };

    const handleClosePreviewModal = () => {
        setIsPreviewModalVisible(false);
        setPreviewTemplate(null);
    };

    const handleClose = () => {
        setIsVisibleTemplateInfo(false);
        setCurrentItem(null);
    };

    const handleReloadTemplates = () => {
        setIsVisibleTemplateInfo(false);
        loadAnamnesisTemplates();
        
        setNotification({
            open: true,
            message: 'Modelo salvo com sucesso!',
            severity: 'success'
        });
    }

    const handleConfirmAction = (action) => {
        if(action == "delete_template") {
            DeleteAnamnesisTemplate(currentItem.id, (response) => {
                handleReloadTemplates();
                setNotification({
                    open: true,
                    message: 'Modelo apagado com sucesso!',
                    severity: 'success'
                });
            });
        }
    }

    const handleConfirmPopupHide = () => {
        setIsVisibleConfirmPopup(false);
        setCurrentItem(null);
    }

    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    return (
        <div className={styles.container}>
            {/* Notificações */}
            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert 
                    onClose={handleCloseNotification} 
                    severity={notification.severity}
                    variant="filled"
                >
                    {notification.message}
                </Alert>
            </Snackbar>

            {/* Popups */}
            {isVisibleConfirmPopup && (
                <ConfirmPopup
                    Visibility={isVisibleConfirmPopup}
                    Title={confirmPopupData.title}
                    Description={confirmPopupData.description}
                    ConfirmLabel={"Apagar"}
                    OnConfirm={() => handleConfirmAction("delete_template")}
                    OnHide={handleConfirmPopupHide}
                />
            )}
            {isVisibleTemplateInfo && (
                <CreateEditAnamnesisTemplate
                    Data={currentItem}
                    OnUpdate={handleReloadTemplates}
                    OnHide={handleClose}
                />
            )}
            {isPreviewModalVisible && (
                <AnamnesisPreviewModal
                    open={isPreviewModalVisible}
                    template={previewTemplate}
                    onClose={handleClosePreviewModal}
                />
            )}
            
            {/* Header */}
            <Box sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                mb: 2
            }}>
                <CustomButton
                    style="secondary"
                    variant="contained"
                    icon={<AddIcon />}
                    label="Adicionar modelo"
                    onClick={handleAddTemplate}
                />
            </Box>
            
            {/* Content */}
            {data.length > 0 ? (
                <div className={styles.templatesGrid}>
                    {data.map((template) => (
                        <AnamnesisCard
                            key={template.id}
                            template={template}
                            onEdit={handleEditTemplate}
                            onDelete={handleDeleteTemplate}
                            onPreview={handlePreviewTemplate}
                        />
                    ))}
                </div>
            ) : (
                <div className={styles.emptyState}>
                    <InfoOutlinedIcon className={styles.emptyStateIcon} />
                    <Typography variant="body1" className={styles.emptyStateText}>
                        Nenhum modelo de anamnese cadastrado
                    </Typography>
                    <CustomButton
                        style="secondary"
                        variant="contained"
                        icon={<AddIcon />}
                        label="Adicionar modelo"
                        onClick={handleAddTemplate}
                    />
                </div>
            )}
        </div>
    );
};

export default AnamnesisTemplates;